import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CommonTable from '../../common/commonTable/CommonTable';
import PaginationComponent from '../../common/pagination/Pagination';
import { deleteMembers, fetchEmployees, inviteMemberInCompany } from '../../../api/companyStructureApi/companyStructureApi';
import CommonModal from '../../common/commonModal/CommonModal';
import ReactNotifications from '../../common/reactNotifications/ReactNotifications';
import * as utilConstants from "../../../utils/constants/utilsConstants";
import validateFields from '../../ValidationChecker';
import { addEmployeeFormCustomFields, employeeFilterConfig } from './common';
import DeleteModal from '../../common/commonModal/DeleteModal';
import { Button } from 'react-bootstrap';
import { RiDeleteBin6Line } from 'react-icons/ri';
import CommonFilter from '../../common/filterComponent/CommonFilter';
import Context from '../../../context/Context';
import MemberInvitationModal from './MemberInvitationModal';

const Employees = () => {
    const [employeesList, setEmployeesList] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [filters, setFilters] = useState({});
    const [targetId, setTargetId] = useState(null);
    const id = localStorage.getItem("company_id");
    const { user, setPageTitle } = useContext(Context);
    const getStatusStyles = (status) => {
        switch (status) {
            case 'Onboarded':
                return { bg: '#e6fffa', color: '#047857' };
            case 'Invited':
                return { bg: '#eff6ff', color: '#1d4ed8' };
            case 'Not Delivered':
                return { bg: '#fef2f2', color: '#b91c1c' };
            default:
                return { bg: '#f3f4f6', color: '#374151' };
        }
    };

    const columns = [
        {
            header: 'Employee Name',
            accessor: row => {
                const initials = `${row.first_name?.charAt(0) || ''}${row.last_name?.charAt(0) || ''}`.toUpperCase();

                return (
                    <div className="d-flex align-items-center gap-2">
                        {row.profile_image ? (
                            <img
                                src={row.profile_image}
                                alt="profile"
                                style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '50%' }}
                            />
                        ) : (
                            <div
                                className="d-flex align-items-center justify-content-center bg-secondary text-white fw-bold"
                                style={{ width: '32px', height: '32px', borderRadius: '50%', fontSize: '12px' }}
                            >
                                {initials}
                            </div>
                        )}
                        <span>{row.first_name} {row.last_name}</span>
                    </div>
                );
            }
        },
        { header: 'Email', accessor: 'email' },
        {
            header: 'Email Status',
            accessor: row => {
                let status = 'Not Delivered';
                if (row.is_onboarded) {
                    status = 'Onboarded';
                } else if (row.email_status?.status === 'processed' || row.email_status?.status === 'delivered') {
                    status = 'Invited';
                }

                const styles = getStatusStyles(status);

                return (
                    <span style={{
                        backgroundColor: styles.bg,
                        color: styles.color,
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'inline-block'
                    }}>
                        {status}
                    </span>
                );
            }
        },
        {
            header: 'Role',
            accessor: row => {
                if (row.role === 'Company Head') return row.role;
                if (row.role === 'Member' && row.team_name) return 'Team Member';
                return (row.role && row.team_name) ? row.role : 'Company Member';
            }
        },
        { header: 'Department', accessor: row => row.team_name || '--' },
    ];
    const addFormFields = [
        { header: 'First Name', accessor: 'first_name' },
        { header: 'Last Name', accessor: 'last_name' },
        { header: 'Member Email', accessor: 'email' },
    ];
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('');
    const [modalData, setModalData] = useState({
        first_name: '',
        last_name: '',
        email: ''
    });
    const [errors, setErrors] = useState({});
    const [showDelModal, setShowDelModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [dynamicConfig, setDynamicConfig] = useState(employeeFilterConfig);
    const [isAdding, setIsAdding] = useState(false);
    const [isFetchingDepartments, setIsFetchingDepartments] = useState(false);


    const handlePageChange = (page) => {

        if (page > 1) {
            searchParams.set('p', page);
        } else {
            searchParams.delete('p');
        }
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
        getEmployeesListing(page, filters);
        setCurrentPage(page);
    };

    const handleOpenModal = (mode) => {
        setModalMode(mode);

        if (mode === 'add') {
            setModalData({
                first_name: '',
                last_name: '',
                email: ''
            });
        }

        setErrors({});
        setShowModal(true);
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        const updatedData = {
            ...modalData,
            [name]: value
        };
        setModalData(updatedData);
        const { errors } = validateFields(updatedData, addEmployeeFormCustomFields);
        setErrors(errors);
    };

    const handleSave = () => {
        const { isValid, errors } = validateFields(modalData, addEmployeeFormCustomFields);

        if (isValid) {
            if (modalMode === 'add') {
                setIsAdding(true);
                inviteMemberInCompany(id, modalData, (res) => {
                    setIsAdding(false);
                    if (res?.status === true || res?.status === 200) {
                        ReactNotifications(res?.message || res?.data?.message, utilConstants.sucessState200);
                        getEmployeesListing();
                        setShowModal(false);
                    } else {
                        if (res?.data?.errors) {
                            setErrors(res?.data?.errors);
                        }
                    }
                });
            }
        } else {
            setErrors(errors);
        }
    };

    const handleDelete = () => {
        if (selectedIds) {
            deleteMembers(id, { user_id: selectedIds }, (res) => {
                if (res?.status === true) {
                    ReactNotifications(res?.message, utilConstants.sucessState200);
                    getEmployeesListing();
                    setSelectedIds([]);
                    setShowDelModal(false);
                } else {
                    ReactNotifications(res?.data?.message, utilConstants.getErrorStatus);
                }
            })
        }
    };

    const getEmployeesListing = (page = 1, appliedFilters = {}) => {
        setLoading(true);
        const payload = { page, ...appliedFilters };
        Object.keys(appliedFilters).forEach(key => {
            if (appliedFilters[key]) {
                payload[key] = appliedFilters[key];
            }
        });
        fetchEmployees(payload, (response) => {
            if (response?.status === true) {
                setTotalCount(response?.count);
                setEmployeesList(response?.data);

                if (response?.data && response.data.length > 0) {
                    const currentUserEmployee = response.data.find(
                        (employee) => employee.user_id === user?.id
                    );
                    if (currentUserEmployee && currentUserEmployee.team_id !== undefined) {
                        setTargetId(currentUserEmployee.team_id);
                    }

                }
            }
            setLoading(false);
            setHasFetched(true);
        });
    };

    useEffect(() => {
        setPageTitle('Employees');
        setIsFetchingDepartments(true);
        fetchEmployees({ page: false }, (response) => {
            if (response?.status === true && response?.data?.length > 0) {
                setAllEmployees(response?.data);
                const uniqueDepartments = [...new Set(
                    response?.data?.map(dept => dept.team_name).filter(name => name != null && name !== '')
                )];
                const departmentOptions = uniqueDepartments.map(name => ({
                    label: name,
                    value: name.toLowerCase()
                }));
                const updatedConfig = employeeFilterConfig.map(filter =>
                    filter.name === 'team_name' ? { ...filter, options: departmentOptions } : filter
                );
                setDynamicConfig(updatedConfig);
            }
            setIsFetchingDepartments(false);
        });
    }, [setPageTitle]);

    useEffect(() => {
        const { searchQuery } = location.state || {};

        if (searchQuery) {
            setFilters({ search: searchQuery });
            setCurrentPage(1);
            navigate(location.pathname, { replace: true, state: {} });
        } else {
            getEmployeesListing(currentPage, filters);
        }
    }, [filters, currentPage, location.state]);

    return (
        <section>
            <div className='px-4'>
                <CommonTable
                    columns={columns}
                    data={employeesList}
                    onAdd={user.role === "Company Head" ? () => handleOpenModal('add') : null}
                    addButtonText="Invite"
                    loading={loading}
                    hasFetched={hasFetched}
                    isExport={false}
                    selectable={user.role === "Company Head"}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    idAccessor="user_id"
                    sortAble={true}
                    sortedCol={["first_name", "last_name", "email"]}
                    bulkActions={[
                        selectedIds.length > 0 && (<Button
                            key="delete"
                            variant='outline-danger'
                            onClick={() => setShowDelModal(true)}
                            className="d-flex align-items-center !py-[10px] !px-[16px] h-[30px] font-bold !rounded-[4px] gap-1"
                        >
                            <RiDeleteBin6Line size={20} />
                            <span>Delete ({selectedIds?.length})</span>
                        </Button>),

                        user.role === "Team Head" && (<MemberInvitationModal employeesList={allEmployees} targetId={targetId} />)
                    ]}
                    filters={<CommonFilter config={dynamicConfig} onApply={setFilters} initialFilters={filters} />}
                />
                {totalCount > 10 && <PaginationComponent
                    currentPage={currentPage}
                    totalCount={totalCount}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />}
                <CommonModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    handleSave={handleSave}
                    saveButtonText="Send Invitation"
                    // icon={<BsFillSendPlusFill className='me-1 mt-1' />}
                    mode={modalMode}
                    columns={addFormFields}
                    data={modalData}
                    handleChange={handleChange}
                    errors={errors}
                    isSaving={isAdding}
                />
                <DeleteModal
                    show={showDelModal}
                    onClose={() => setShowDelModal(false)}
                    onConfirm={handleDelete}
                    title={selectedIds?.length > 1 ? `Delete Members` : `Delete Member`}
                    message={`Are you sure you want to delete ${selectedIds?.length > 1 ? `these members from organization?` : `this member from organization?`} This action cannot be undone.`}
                />
            </div>
        </section>
    )
}

export default Employees;